import { Space, Typography, Alert } from "antd";
import { useLoaderData, useFetcher } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LifecycleAccessTabLoaderType } from "../../loaders/LifecycleAccessTabLoader";
import * as APITypes from "~/API";
import { ColumnsType } from "antd/es/table";
import { dateToLocalTime, INTENTS } from "~/utils";
import { AccessRightStatusTag, CustomTable } from "~/components";
import { AccessStatusDropdown } from "../AccessStatusDropdown";

// All available entity types for lifecycle access
const ALL_ENTITY_TYPES = [
  APITypes.EntityType.Property,
  APITypes.EntityType.Unit,
  APITypes.EntityType.Building,
  APITypes.EntityType.User,
  APITypes.EntityType.Tenant,
  APITypes.EntityType.ServiceRequest,
  APITypes.EntityType.Amenity,
  APITypes.EntityType.UnitPlan,
  APITypes.EntityType.Transaction,
  APITypes.EntityType.Report,
  APITypes.EntityType.Lease,
  APITypes.EntityType.Stay,
  APITypes.EntityType.Parcel,
];

type LifecycleAccessRow = {
  key: string;
  resourceType: APITypes.EntityType;
  createAccessStatus: APITypes.ResourceAccessRightStatus;
  deleteAccessStatus: APITypes.ResourceAccessRightStatus;
  lastModified: string;
  grantedDate: string;
  accessRight?: APITypes.ResourceLifeAccessRight;
};

export const LifecycleAccessTab = () => {
  const { t } = useTranslation();
  const loaderData = useLoaderData() as LifecycleAccessTabLoaderType;
  const fetcher = useFetcher();
  const isLoading =
    fetcher.state === "submitting" || fetcher.state === "loading";

  // Create a map of existing access rights by resource type
  const accessRightsMap = new Map(
    loaderData.accessRights.map((right) => [right.resourceType, right])
  );

  // Build table data with all entity types
  const tableData: LifecycleAccessRow[] = ALL_ENTITY_TYPES.map((entityType) => {
    const existingRight = accessRightsMap.get(entityType);

    return {
      key: entityType,
      resourceType: entityType,
      createAccessStatus:
        existingRight?.createAccessStatus ||
        APITypes.ResourceAccessRightStatus.NotGranted,
      deleteAccessStatus:
        existingRight?.deleteAccessStatus ||
        APITypes.ResourceAccessRightStatus.NotGranted,
      lastModified: existingRight?.updatedAt || "",
      grantedDate: existingRight?.createdAt || "",
      accessRight: existingRight,
    };
  });

  const handleUpdateStatus = (
    resourceType: APITypes.EntityType,
    accessType: "create" | "delete",
    newStatus: APITypes.ResourceAccessRightStatus
  ) => {
    const formData = new FormData();
    formData.append("intent", INTENTS._update_lifecycle_access_status);
    formData.append(
      "body",
      JSON.stringify({
        resourceType,
        accessType,
        status: newStatus,
      })
    );

    fetcher.submit(formData, {
      method: "post",
      action: window.location.pathname,
    });
  };

  const getResourceTypeLabel = (resourceType: APITypes.EntityType) => {
    return t(resourceType as any) || resourceType;
  };

  const columns: ColumnsType<LifecycleAccessRow> = [
    {
      title: t("Resource_Type"),
      dataIndex: "resourceType",
      key: "resourceType",
      width: 200,
      render: (resourceType: APITypes.EntityType) => (
        <Typography.Text strong>
          {getResourceTypeLabel(resourceType)}
        </Typography.Text>
      ),
    },
    {
      title: t("Create_Access"),
      dataIndex: "createAccessStatus",
      key: "createAccessStatus",
      width: 150,
      align: "center",
      render: (status: APITypes.ResourceAccessRightStatus) => (
        <AccessRightStatusTag status={status} />
      ),
    },
    {
      title: t("Delete_Access"),
      dataIndex: "deleteAccessStatus",
      key: "deleteAccessStatus",
      width: 150,
      align: "center",
      render: (status: APITypes.ResourceAccessRightStatus) => (
        <AccessRightStatusTag status={status} />
      ),
    },
    {
      title: t("Actions"),
      key: "actions",
      width: 200,
      render: (_, record) => {
        return (
          <Space direction="horizontal" size="small" style={{ width: "100%" }}>
            {/* Create Access Actions */}
            <AccessStatusDropdown
              currentStatus={record.createAccessStatus}
              onChange={(newStatus) =>
                handleUpdateStatus(record.resourceType, "create", newStatus)
              }
              label={t("Create")}
              isLoading={isLoading}
              isAdmin={loaderData.canUpdateMemberRights}
            />

            {/* Delete Access Actions */}
            <AccessStatusDropdown
              currentStatus={record.deleteAccessStatus}
              onChange={(newStatus) =>
                handleUpdateStatus(record.resourceType, "delete", newStatus)
              }
              label={t("Delete")}
              isLoading={isLoading}
              isAdmin={loaderData.canUpdateMemberRights}
            />
          </Space>
        );
      },
    },
    {
      title: t("Last_Modified"),
      dataIndex: "lastModified",
      key: "lastModified",
      width: 200,
      render: (date: string) =>
        date ? dateToLocalTime(date, "dateTime") : "-",
    },
    {
      title: t("Granted_Date"),
      dataIndex: "grantedDate",
      key: "grantedDate",
      width: 200,
      render: (date: string) =>
        date ? dateToLocalTime(date, "dateTime") : "-",
    },
  ];

  // If user is Admin or SuperAdmin, show message instead of table
  if (loaderData.isAdminOrSuperAdmin) {
    return (
      <div>
        <Space direction="vertical" style={{ width: "100%", marginBottom: 16 }}>
          <Typography.Title level={5}>{t("Lifecycle_Access")}</Typography.Title>
          <Typography.Text type="secondary">
            {t("Lifecycle_Access_Description")}
          </Typography.Text>
        </Space>

        <Alert
          message={t("Admin_Auto_Access_Title")}
          description={t("Admin_Auto_Access_Description")}
          type="info"
          showIcon
        />
      </div>
    );
  }

  const tableHeaders = (
    <Space direction="vertical" style={{ width: "100%" }}>
      <Typography.Title level={5}>{t("Lifecycle_Access")}</Typography.Title>
      <Typography.Text type="secondary">
        {t("Lifecycle_Access_Description")}
      </Typography.Text>
    </Space>
  );

  return (
    <CustomTable
      tableHeaders={tableHeaders}
      tableProps={{
        columns,
        dataSource: tableData,
        loading: isLoading,
        pagination: false,
      }}
    />
  );
};
